<template>
  <AM3Modal
    :is-open="isOpen"
    width="sm"
    header="Order Details"
    @close="$emit('close')"
  >
    <template #body>
      <div
        v-if="modalData"
        class=""
      >
        <div class="space-y-6">
          <!-- Order Header -->
          <div class="flex w-full flex-col items-center rounded-md bg-surfaces-neutral p-2 md:p-4 text-center sm:flex-row sm:justify-stretch sm:p-4 sm:text-left">
            <div class="flex w-full flex-col items-center sm:flex-row">
              <div class="bg-brand-light text-brand outline-surface-light p-2 size-8 rounded-full mr-3 flex flex-row items-center justify-center">
                <AM3Icon
                  :type="getIconForPlatform(modalData.source?.platform)"
                  size="lg"
                />
              </div>
              <div class="py-2 sm:py-0">
                <p>{{ modalData.source?.platform || 'Unknown' }}</p>
                <p class="text-secondary text-xs">
                  {{ modalData.description }}
                </p>
              </div>
            </div>
            <div class="w-full sm:text-right">
              <p>Order ID {{ modalData.orderId }}</p>
              <p class="text-secondary text-xs">
                <span>Purchased On </span>
                <AM3Formatter
                  :value="modalData.purchaseDateTime"
                  type="datetime"
                  :options="{ dateStyle: 'medium', timeStyle: 'short' }"
                />
              </p>
            </div>
          </div>

          <!-- Summary Heading -->
          <h3 class="text-center sm:text-left">
            Order Summary
          </h3>

          <!-- Line Items -->
          <div
            v-for="(item, index) in modalData.lineItems"
            :key="index"
            class="flex flex-col items-center sm:flex-row sm:justify-between"
          >
            <div class="flex flex-col items-center justify-between space-y-4 sm:flex-row sm:space-y-0">
              <div class="relative mr-4">
                <div
                  v-if="modalData.imageUrl || modalData.fallbackImage"
                  class="relative"
                >
                  <img
                    :src="modalData.imageUrl || modalData.fallbackImage"
                    :alt="modalData.description"
                    class="size-20 rounded-xs object-contain"
                    width="80"
                    height="80"
                    loading="lazy"
                  >
                  <AM3Badge
                    :show="item.quantity >= 1"
                    type="inverse"
                  >
                    {{ item.quantity || 1 }}
                  </AM3Badge>
                </div>
                <div
                  v-else
                  class="size-20 relative bg-gray-200 rounded-xs flex items-center justify-center"
                >
                  <AM3Icon
                    name="calendar"
                    class="w-8 h-8 text-gray-400"
                  />
                  <AM3Badge
                    :show="item.quantity >= 1"
                    type="inverse"
                  >
                    {{ item.quantity || 1 }}
                  </AM3Badge>
                </div>
              </div>
              <div class="text-center sm:text-left">
                {{ item.ticketType || 'General Admission' }}
              </div>
            </div>
            <div>
              <AM3Formatter
                :value="safeParseFloat(item.subtotalPrice || item.totalPrice)"
                type="currency"
                :options="{ code: modalData.currencyCode }"
              />
            </div>
          </div>

          <!-- Subtotal -->
          <div class="flex flex-row items-center justify-between">
            <div class="flex flex-row items-center justify-between">
              Subtotal
            </div>
            <div>
              <AM3Formatter
                :value="calculateSubtotal()"
                type="currency"
                :options="{ code: modalData.currencyCode }"
              />
            </div>
          </div>

          <!-- Fees -->
          <div
            v-if="safeParseFloat(modalData.fees) > 0"
            class="flex flex-row items-center justify-between"
          >
            <div class="flex flex-row items-center justify-between">
              Fees
            </div>
            <div>
              <AM3Formatter
                :value="safeParseFloat(modalData.fees)"
                type="currency"
                :options="{ code: modalData.currencyCode }"
              />
            </div>
          </div>

          <!-- Taxes -->
          <div
            v-if="safeParseFloat(modalData.tax) > 0"
            class="flex flex-row items-center justify-between"
          >
            <div class="flex flex-row items-center justify-between">
              Taxes
            </div>
            <div>
              <AM3Formatter
                :value="safeParseFloat(modalData.tax)"
                type="currency"
                :options="{ code: modalData.currencyCode }"
              />
            </div>
          </div>

          <!-- Total -->
          <div class="flex flex-row items-center justify-between border-t border-solid border-gray-100 pt-4 text-xl">
            <div class="flex flex-row items-center justify-between">
              Total
            </div>
            <div>
              <AM3Formatter
                :value="safeParseFloat(modalData.grossTotal)"
                type="currency"
                :options="{ code: modalData.currencyCode }"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <AM3Button
        text="Got it"
        type="primary"
        :data-test-id="generateDataTestId('order-details', 'modal', 'close-button')"
        @click="handleClose"
      />
    </template>
  </AM3Modal>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import AM3Modal from '@/components/am3-generic/layout/AM3Modal.vue'
import AM3Button from '@/components/am3-generic/base/AM3Button.vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import AM3Formatter from '@/components/am3-generic/data/AM3Formatter.vue'
import AM3Badge from '@/components/am3-generic/base/AM3Badge.vue'

import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'

interface Props {
  isOpen: boolean
  modalData: any
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
}>()

const handleClose = () => {
  emit('close')
}

const safeParseFloat = (value: any): number => {
  if (value === undefined || value === null || value === '') return 0
  const parsed = parseFloat(value)
  return isNaN(parsed) ? 0 : parsed
}

const getIconForPlatform = (platform: string | undefined): string => {
  if (!platform) return 'ticket'

  const platformLower = platform.toLowerCase()

  if (platformLower.includes('eventbrite')) return 'ticket'
  if (platformLower.includes('ticketek')) return 'ticket'
  if (platformLower.includes('csv')) return 'arrow-down-tray'
  if (platformLower.includes('pos')) return 'shopping-cart'

  return 'ticket'
}

const calculateSubtotal = (): number => {
  if (!props.modalData || !props.modalData.lineItems) return 0
  return props.modalData.lineItems.reduce((sum: number, item: any) => {
    const itemPrice = safeParseFloat(item.subtotalPrice || item.totalPrice)
    return sum + itemPrice
  }, 0)
}
</script>
